import React, { useState, useMemo, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Link,
} from '@mui/material';
import UpgradeRequestDialog from '../UpgradeRequestDialog';
import {
  Info as InfoIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

// Map table names from database to frontend display names
const tableNameMap = {
  'app_texas_wells': 'wells',
  'app_texas_production': 'production',
  'app_texas_inspections': 'inspections',
  'app_texas_violations': 'violations',
  'app_texas_severances': 'severances',
  'app_texas_spills': 'spills',
};

const displayNameMap = {
  'wells': 'Wells',
  'production': 'Production',
  'inspections': 'Inspections',
  'violations': 'Violations',
  'severances': 'Severances',
  'spills': 'Spills',
};

const MatchesCard = ({
  searchResults,
  searchParams,
  activityDateFilters,
  wellDateFilters,
  formatTableResult,
  getUniqueOperatorNames,
  setOperatorNamesDialogOpen,
  tierInfo,
  setActiveTab,
}) => {
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  
  // Helper function to calculate tab index for each table type
  const getTabIndex = useCallback((tableType) => {
    // Calculate which tables are available (have data)
    const availableTableTypes = ['wells', 'production', 'inspections', 'violations', 'severances', 'spills']
      .filter(type => {
        const data = searchResults.data?.[type];
        return data && Array.isArray(data) && data.length > 0;
      });
    
    const currentIndex = availableTableTypes.indexOf(tableType);
    
    if (currentIndex === -1) return 2; // Shouldn't happen, but fallback
    
    // Start at 2 (after Matches=0, Summary=1), then add index within available tables
    return 2 + currentIndex;
  }, [searchResults?.data]);

  // Date formatting utility: Convert YYYY-MM-DD or YYYY-MM to "MMM YYYY"
  const formatDateToMMMYYYY = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Handle YYYY-MM-DD or YYYY-MM format
      const dateParts = dateString.split('-');
      const year = dateParts[0];
      const month = parseInt(dateParts[1], 10);
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[month-1]} ${year}`;
    } catch (e) {
      return dateString; // Return original if parsing fails
    }
  };

  // Get table date ranges from search results
  const tableDateRanges = useMemo(() => {
    const ranges = {};
    if (searchResults?.table_date_ranges) {
      searchResults.table_date_ranges.forEach(range => {
        const tableKey = tableNameMap[range.table_name];
        if (tableKey) {
          ranges[tableKey] = {
            start_date: range.start_date,
            end_date: range.end_date,
          };
        }
      });
    }
    return ranges;
  }, [searchResults?.table_date_ranges]);

  // Get search date range for a table
  // Uses activityDateFilters/wellDateFilters if defined, otherwise falls back to table_date_ranges
  const getSearchDateRange = useCallback((tableKey) => {
    let startDate = null;
    let endDate = null;
    let userDefinedDates = false;

    // Helper to get valid date parameter (same logic as in useSearchData)
    const getValidDateParam = (dateValue) => {
      if (dateValue && dateValue.includes('-') && dateValue.split('-')[1] && dateValue.split('-')[1].length === 2) {
        return dateValue;
      }
      return null;
    };

    switch (tableKey) {
      case 'wells':
        startDate = getValidDateParam(wellDateFilters?.start_date);
        endDate = getValidDateParam(wellDateFilters?.end_date);
        userDefinedDates = !!(startDate || endDate);
        break;
      case 'production':
        startDate = getValidDateParam(activityDateFilters?.start_date);
        endDate = getValidDateParam(activityDateFilters?.end_date);
        userDefinedDates = !!(startDate || endDate);
        break;
      case 'inspections':
        startDate = getValidDateParam(activityDateFilters?.start_date);
        endDate = getValidDateParam(activityDateFilters?.end_date);
        userDefinedDates = !!(startDate || endDate);
        break;
      case 'violations':
        startDate = getValidDateParam(activityDateFilters?.start_date);
        endDate = getValidDateParam(activityDateFilters?.end_date);
        userDefinedDates = !!(startDate || endDate);
        break;
      case 'severances':
        startDate = getValidDateParam(activityDateFilters?.start_date);
        endDate = getValidDateParam(activityDateFilters?.end_date);
        userDefinedDates = !!(startDate || endDate);
        break;
      case 'spills':
        startDate = getValidDateParam(activityDateFilters?.start_date);
        endDate = getValidDateParam(activityDateFilters?.end_date);
        userDefinedDates = !!(startDate || endDate);
        break;
      default:
        // No action needed for unknown table keys
        break;
    }

    // Special handling for Wells
    if (tableKey === 'wells') {
      const dbRange = tableDateRanges[tableKey];
      const dbEndDate = dbRange?.end_date;
      
      if (!userDefinedDates) {
        // No search params defined: "All through {db_end_date}"
        if (dbEndDate) {
          const formattedEnd = formatDateToMMMYYYY(dbEndDate);
          return `All through ${formattedEnd}`;
        }
        return null;
      } else {
        // User defined dates
        if (!startDate && endDate) {
          // Only end date defined: "All through {end_date}"
          const formattedEnd = formatDateToMMMYYYY(endDate);
          return `All through ${formattedEnd}`;
        } else if (startDate && !endDate) {
          // Only start date defined: "{start_date}-{db_end_date}"
          const formattedStart = formatDateToMMMYYYY(startDate);
          if (dbEndDate) {
            const formattedEnd = formatDateToMMMYYYY(dbEndDate);
            return `${formattedStart}-${formattedEnd}`;
          }
          return formattedStart;
        } else {
          // Both dates defined: "{start_date}-{end_date}"
          const formattedStart = formatDateToMMMYYYY(startDate);
          const formattedEnd = formatDateToMMMYYYY(endDate);
          return `${formattedStart}-${formattedEnd}`;
        }
      }
    }

    // For other tables: if user defined dates, use them (supplement missing ones with table_date_ranges if needed)
    if (userDefinedDates) {
      // Supplement missing dates with table_date_ranges
      if (!startDate || !endDate) {
        const dbRange = tableDateRanges[tableKey];
        if (dbRange) {
          if (!startDate) startDate = dbRange.start_date;
          if (!endDate) endDate = dbRange.end_date;
        }
      }
      // If both dates are provided by user, use them directly (no supplementation needed)
    } else {
      // If user didn't define dates, use table_date_ranges
      const dbRange = tableDateRanges[tableKey];
      if (dbRange) {
        startDate = dbRange.start_date;
        endDate = dbRange.end_date;
      }
    }

    // If still no dates, return null
    if (!startDate && !endDate) {
      return null;
    }

    const formattedStart = startDate ? formatDateToMMMYYYY(startDate) : null;
    const formattedEnd = endDate ? formatDateToMMMYYYY(endDate) : null;

    if (formattedStart && formattedEnd) {
      return `${formattedStart}-${formattedEnd}`;
    } else if (formattedStart) {
      return formattedStart;
    } else if (formattedEnd) {
      return formattedEnd;
    }
    return null;
  }, [activityDateFilters, wellDateFilters, tableDateRanges]);

  // Build table rows with limit tracking
  const { tableRows, limitNotes } = useMemo(() => {
    const rows = [];
    const notes = [];
    let noteCounter = 1;

    const tableOrder = ['wells', 'production', 'inspections', 'violations', 'severances', 'spills'];
    const searchParamMap = {
      'wells': 'search_wells',
      'production': 'search_production',
      'inspections': 'search_inspections',
      'violations': 'search_violations',
      'severances': 'search_severances',
      'spills': 'search_spills',
    };

    const defaultLimits = {
      'wells': 500,
      'production': 2500,
      'inspections': 1000,
      'violations': 500,
      'severances': 500,
      'spills': 100,
    };

    tableOrder.forEach(tableKey => {
      if (searchParams[searchParamMap[tableKey]]) {
        const count = searchResults.counts?.[tableKey] || 0;
        const limit = tierInfo?.table_limits?.[tableKey] || defaultLimits[tableKey];
        const hasData = count > 0;
        const isAtLimit = count >= limit;
        const dateRange = getSearchDateRange(tableKey);
        
        // Add note if at limit
        let noteNumber = null;
        if (isAtLimit) {
          noteNumber = noteCounter++;
          notes.push({
            number: noteNumber,
            tableName: displayNameMap[tableKey],
            limit: limit,
          });
        }

        // Date column descriptions for display
        const dateColumnDisplayNames = {
          'wells': 'Drill dates (if available)',
          'production': 'Production months',
          'inspections': 'Inspection dates',
          'violations': 'Violation discovery dates',
          'severances': 'Certified letter dates',
          'spills': 'Date of loss',
        };

        rows.push({
          key: tableKey,
          tableName: displayNameMap[tableKey],
          count: count,
          dateRange: dateRange,
          dateColumnName: dateColumnDisplayNames[tableKey],
          noteNumber: noteNumber,
          onClick: hasData ? () => setActiveTab(getTabIndex(tableKey)) : undefined,
          isClickable: hasData,
        });
      }
    });

    return { tableRows: rows, limitNotes: notes };
  }, [searchResults, searchParams, tierInfo, getTabIndex, setActiveTab, getSearchDateRange]);

  if (!searchResults) return null;

  return (
    <Paper elevation={0} sx={{ 
      p: 2, 
      mb: 3,
      width: '100%',
      overflow: 'hidden',
      position: 'relative',
      border: 1,
      borderColor: 'divider'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AssignmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
        <Typography variant="h5" component="h2">
          Matched Records
        </Typography>
      </Box>

      {/* Content */}
      <Box>
        {/* Unique Operator Names */}
        <Box sx={{ mb: 2, textAlign: 'left' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>
              Unique Operator Names — {getUniqueOperatorNames().length.toLocaleString()}
            </Typography>
            <Tooltip title="View all unique operator names" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => setOperatorNamesDialogOpen(true)}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Divider */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, mt: 2 }} />
          
          <Box sx={{ mt: 2, textAlign: 'left' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Link
                component="button"
                onClick={() => setActiveTab(1)} // Report Card tab
                sx={{ 
                  textDecoration: 'none',
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontWeight: 700,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Report Card
              </Link>
              <Typography sx={{ fontSize: '0.875rem' }}>
                — {Object.values(searchResults.counts || {}).reduce((sum, count) => sum + (count || 0), 0).toLocaleString()} total records
              </Typography>
            </Box>
          </Box>

          {/* Footer: Date range explanation */}
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Date ranges reflect activity dates. If no date ranges are defined in search parameters, search defaults to the full dataset date range.
            </Typography>
          </Box>
        </Box>

        {/* Table rows */}
        {tableRows.map((row) => {
          const noteSuperscript = row.noteNumber ? (
            <sup style={{ fontSize: '0.75em', verticalAlign: 'super' }}>{row.noteNumber}</sup>
          ) : '';
          
          return (
            <Box key={row.key} sx={{ mb: 2, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                {row.isClickable ? (
                  <Link
                    component="button"
                    onClick={row.onClick}
                    sx={{ 
                      textDecoration: 'none',
                      color: 'primary.main',
                      cursor: 'pointer',
                      fontWeight: 700,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {row.tableName}
                  </Link>
                ) : (
                  <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {row.tableName}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.875rem' }}>
                  — {row.count.toLocaleString()} records{noteSuperscript}
                </Typography>
              </Box>
              {row.dateRange && row.dateColumnName && (
                <Box sx={{ pl: 2, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {row.dateColumnName}: {row.dateRange}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}

        {/* Limit notes */}
        {limitNotes.length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            {limitNotes.map((note) => (
              <Typography key={note.number} variant="body2" sx={{ fontSize: '0.7rem', color: 'error.main', mb: 0.25 }}>
                {note.number}. Limited to first {note.limit.toLocaleString()} for {note.tableName}
              </Typography>
            ))}
            {tierInfo?.tier !== 'pro' && (
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'error.main', mt: 1, fontStyle: 'italic' }}>
                Upgrade to Pro for expanded record result and analytics limits
              </Typography>
            )}
          </Box>
        )}

      </Box>
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </Paper>
  );
};

export default MatchesCard;
